'use client';

import { logout, User } from '@/app/actions/user';
import { ThemeSwitch } from '@/components/theme-switch';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { getUserAvatarSrc } from '@/lib/avatar';
import { MCP_URL, OPENAPI_URL } from '@/lib/constants';
import { useAuth } from '@/providers/auth-provider';
import dayjs from 'dayjs';
import {
  BookOpenIcon,
  CircleStopIcon,
  CreditCardIcon,
  HistoryIcon,
  KeyRoundIcon,
  LogOutIcon,
  PaletteIcon,
  ReceiptTextIcon,
  Share2Icon,
  SquareArrowOutUpRightIcon,
  UserIcon,
  UserPlusIcon,
} from 'lucide-react';
import Link from 'next/link';
import { useState } from 'react';
import { McpIcon } from './icons';

interface UserDropdownProps {
  user: User;
}

const UserDropdown = ({ user }: UserDropdownProps) => {
  const { showActivateCardModal } = useAuth();
  const [isOpenCredits, setIsOpenCredits] = useState(false);
  const [isOpenProfile, setIsOpenProfile] = useState(false);

  const hasQueuedCards =
    user.pointInfo?.queued_cards && user.pointInfo.queued_cards.length > 0;
  const canActivate =
    hasQueuedCards && (user.pointInfo?.can_activate_card ?? false);

  return (
    <>
      <DropdownMenu open={isOpenCredits} onOpenChange={setIsOpenCredits}>
        <DropdownMenuTrigger asChild>
          <Button size="sm" variant="outline" className="h-7 text-xs">
            <CircleStopIcon className="w-3 h-3" />
            <span className="font-medium text-sm">
              {user?.pointInfo?.available.toLocaleString()}
            </span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="rounded-lg shadow-none">
          <DropdownMenuLabel>我的积分</DropdownMenuLabel>
          <DropdownMenuGroup>
            <DropdownMenuItem
              className="opacity-100 cursor-default"
              onSelect={(e) => e.preventDefault()}
            >
              <span>可用积分</span>
              <span className="ml-auto opacity-60 font-medium">
                {user.pointInfo.available.toLocaleString()}
              </span>
            </DropdownMenuItem>
            <DropdownMenuItem
              className="opacity-100 cursor-default"
              onSelect={(e) => e.preventDefault()}
            >
              <span>累计获取</span>
              <span className="ml-auto opacity-60 font-medium">
                {user.pointInfo.accumulated.toLocaleString()}
              </span>
            </DropdownMenuItem>
            <DropdownMenuItem
              className="opacity-100 cursor-default"
              onSelect={(e) => e.preventDefault()}
            >
              <span>API积分</span>
              <span className="ml-auto opacity-60 font-medium">
                {user.apiPointInfo?.available?.toLocaleString() || 0}
              </span>
            </DropdownMenuItem>
          </DropdownMenuGroup>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            className="p-0"
            onSelect={(e) => e.preventDefault()}
          >
            <Button
              size="sm"
              color="primary"
              className="w-full h-7"
              asChild
              onClick={() => setIsOpenCredits(false)}
            >
              <Link href="/pricing/credits">购买积分</Link>
            </Button>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <DropdownMenu open={isOpenProfile} onOpenChange={setIsOpenProfile}>
        <DropdownMenuTrigger asChild>
          <div className="flex items-center gap-2 my-1 cursor-pointer">
            <Avatar className="h-7 w-7 shrink-0">
              <AvatarImage
                src={getUserAvatarSrc(user)}
                alt={user.nickname}
              />
              <AvatarFallback>{user.nickname?.[0] || '?'}</AvatarFallback>
            </Avatar>
          </div>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="rounded-lg shadow-none">
          <DropdownMenuGroup>
            <DropdownMenuItem
              className="opacity-100 cursor-default"
              onSelect={(e) => e.preventDefault()}
            >
              <span>{user.nickname}</span>
              <span className="ml-auto">
                {user.membership ? (
                  <span className="px-2 py-0.5 bg-secondary-foreground text-secondary rounded text-xs shrink-0">
                    {user.pointInfo?.tier_name || '高级版'}
                  </span>
                ) : (
                  <span className="px-2 py-0.5 bg-secondary text-secondary-foreground rounded text-xs shrink-0">
                    {user.pointInfo?.tier_name || '免费版'}
                  </span>
                )}
              </span>
            </DropdownMenuItem>
            {user.membership ? (
              <DropdownMenuItem
                className="opacity-100 cursor-default mb-2"
                onSelect={(e) => e.preventDefault()}
              >
                <span>会员到期</span>
                <span className="ml-auto text-muted-foreground">
                  {dayjs(user.membership_expire_time).format('YYYY-MM-DD')}
                </span>
              </DropdownMenuItem>
            ) : null}
            <DropdownMenuItem
              className="p-0"
              onSelect={(e) => e.preventDefault()}
            >
              {canActivate ? (
                <Button
                  variant="outline"
                  size="sm"
                  className="h-7 w-full"
                  onClick={() => {
                    setIsOpenProfile(false);
                    showActivateCardModal();
                  }}
                >
                  激活会员卡
                </Button>
              ) : (user.pointInfo?.tier_rank ?? 0) <
                (user.pointInfo?.max_tier_rank ?? 999) ? (
                <Button
                  variant="outline"
                  size="sm"
                  className="h-7 w-full"
                  asChild
                  onClick={() => setIsOpenProfile(false)}
                >
                  <Link
                    href={
                      user.membership && !user.pointInfo?.can_activate_card
                        ? '/card-pack'
                        : '/pricing/subscribe'
                    }
                  >
                    升级
                  </Link>
                </Button>
              ) : null}
            </DropdownMenuItem>
          </DropdownMenuGroup>
          <DropdownMenuSeparator />
          <DropdownMenuGroup>
            <DropdownMenuItem asChild>
              <Link href="/profile" className="cursor-pointer">
                <UserIcon className="mr-2 h-4 w-4" />
                个人中心
              </Link>
            </DropdownMenuItem>
            <DropdownMenuItem asChild>
              <Link
                href="/card-pack"
                className="cursor-pointer flex items-center"
              >
                <CreditCardIcon className="mr-2 h-4 w-4" />
                我的卡包
                <span className="ml-auto text-[10px] font-medium bg-red-500 text-white px-1.5 py-0.5 rounded-full leading-none">
                  NEW
                </span>
              </Link>
            </DropdownMenuItem>
            <DropdownMenuItem asChild>
              <Link href="/histories" className="cursor-pointer">
                <HistoryIcon className="mr-2 h-4 w-4" />
                历史记录
              </Link>
            </DropdownMenuItem>
            <DropdownMenuItem asChild>
              <Link href="/my-share" className="cursor-pointer">
                <Share2Icon className="mr-2 h-4 w-4" />
                我的分享
              </Link>
            </DropdownMenuItem>
            <DropdownMenuItem asChild>
              <Link href="/credits/billing" className="cursor-pointer">
                <ReceiptTextIcon className="mr-2 h-4 w-4" />
                积分明细
              </Link>
            </DropdownMenuItem>
            <DropdownMenuItem asChild>
              <Link href="/my-invite" className="cursor-pointer">
                <UserPlusIcon className="mr-2 h-4 w-4" />
                我的邀请
              </Link>
            </DropdownMenuItem>
          </DropdownMenuGroup>
          <DropdownMenuSeparator />
          <DropdownMenuGroup>
            <DropdownMenuItem asChild>
              <Link href="/api-keys" className="cursor-pointer">
                <KeyRoundIcon className="mr-2 h-4 w-4" />
                API Keys
              </Link>
            </DropdownMenuItem>
            <DropdownMenuItem asChild>
              <a href={OPENAPI_URL} target="_blank" className="cursor-pointer">
                <BookOpenIcon className="mr-2 h-4 w-4" />
                接口文档
                <SquareArrowOutUpRightIcon className="ml-auto h-4 w-4" />
              </a>
            </DropdownMenuItem>
            <DropdownMenuItem asChild>
              <a href={MCP_URL} target="_blank" className="cursor-pointer">
                <McpIcon className="mr-2 h-4 w-4" />
                MCP 服务
                <SquareArrowOutUpRightIcon className="ml-auto h-4 w-4" />
              </a>
            </DropdownMenuItem>
          </DropdownMenuGroup>
          <DropdownMenuSeparator />
          <DropdownMenuGroup>
            <DropdownMenuItem
              className="cursor-default"
              onSelect={(e) => e.preventDefault()}
            >
              <PaletteIcon className="mr-2 h-4 w-4" />
              <span>主题</span>
              <span className="ml-auto">
                <ThemeSwitch />
              </span>
            </DropdownMenuItem>
          </DropdownMenuGroup>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onClick={async () => {
              await logout();
              window.location.href = '/';
            }}
          >
            <LogOutIcon className="mr-2 h-4 w-4" />
            退出登录
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </>
  );
};

export default UserDropdown;
